/**
 * Monitoring Service - Azure Application Insights Integration
 *
 * Provides centralized error logging, event tracking, and performance monitoring
 * for the Insightly Archive application.
 *
 * Features:
 * - Error tracking with context and categorization
 * - Custom event tracking
 * - Performance monitoring
 * - User context management
 * - Automatic correlation with Azure Functions backend
 * - Error sanitization (removes sensitive data)
 *
 * @module services/monitoring
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

/**
 * Error severity levels
 * Aligned with Application Insights severity levels
 */
export const ErrorSeverity = {
    CRITICAL: 4,  // System is unusable, requires immediate action
    ERROR: 3,     // Error conditions, needs attention
    WARNING: 2,   // Warning conditions, potential issues
    INFO: 1,      // Informational messages
    VERBOSE: 0    // Detailed diagnostic information
};

/**
 * Error categories for better organization and filtering
 */
export const ErrorCategory = {
    AUTHENTICATION: 'Authentication',
    API: 'API',
    UPLOAD: 'Upload',
    DOWNLOAD: 'Download',
    SEARCH: 'Search',
    UI: 'UI',
    NETWORK: 'Network',
    VALIDATION: 'Validation',
    PERMISSION: 'Permission',
    STORAGE: 'Storage',
    UNKNOWN: 'Unknown'
};

/**
 * MonitoringService class
 * Singleton service for Application Insights integration
 */
class MonitoringService {
    constructor() {
        this.appInsights = null;
        this.initialized = false;
        this.config = null;
        this.userContext = null;
        this.operationStack = new Map(); // Track in-progress operations
    }

    /**
     * Initialize Application Insights
     *
     * @param {Object} config - Monitoring configuration from monitoring-config.js
     * @param {string} config.connectionString - Application Insights connection string
     * @param {string} config.environment - Environment name (staging/production)
     * @param {boolean} config.enableDebug - Enable debug logging
     * @param {boolean} config.enableAutoRouteTracking - Auto track route changes
     * @param {number} config.samplingPercentage - Telemetry sampling percentage
     * @returns {boolean} - True if initialization successful
     */
    initialize(config) {
        if (this.initialized) {
            console.warn('⚠️ Monitoring service already initialized');
            return true;
        }

        if (!config || !config.connectionString) {
            console.error('❌ Monitoring config missing or invalid');
            return false;
        }

        try {
            this.config = config;

            // Initialize Application Insights SDK
            this.appInsights = new ApplicationInsights({
                config: {
                    connectionString: config.connectionString,
                    // 🔥 COST OPTIMIZATION: Sample only 5% of telemetry (was 100%)
                    samplingPercentage: config.samplingPercentage || 5,
                    // 🔥 DISABLE: Auto route tracking (track manually for important routes only)
                    enableAutoRouteTracking: false,
                    // 🔥 DISABLE: Automatic fetch/XHR tracking (you already manually track with trackAPICall)
                    disableFetchTracking: true,
                    disableAjaxTracking: true,
                    // 🔥 DISABLE: Header tracking (rarely needed, huge overhead)
                    enableRequestHeaderTracking: false,
                    enableResponseHeaderTracking: false,
                    // 🔥 DISABLE: CORS correlation (not needed for same-domain calls)
                    enableCorsCorrelation: false,
                    // ✅ KEEP: Exception tracking (critical for debugging)
                    disableExceptionTracking: false,
                    enableUnhandledPromiseRejectionTracking: true,
                    // ✅ KEEP: Batch settings
                    maxBatchInterval: config.maxBatchInterval || 30000, // Increased from 15s to 30s (less frequent sends)
                    maxBatchSizeInBytes: config.maxBatchSizeInBytes || 1000000,
                    // ✅ KEEP: Debug settings
                    enableDebug: config.enableDebug || false,
                    loggingLevelConsole: config.enableDebug ? 2 : 0,
                    loggingLevelTelemetry: 0, // Set to 0 to reduce SDK internal telemetry
                    // 🔥 DISABLE: Internal SDK Stats (3.4.5+). Sends to a Microsoft endpoint when
                    // remote config enables it, bypassing sanitizeTelemetry. mode 2 = FeatureOptInMode.disable;
                    // blockCdnCfg stops the remote config forcing it back on (CR-2026-11-01 §3.4)
                    featureOptIn: { sdkStats: { mode: 2, blockCdnCfg: true } }
                }
            });

            // Load and enable Application Insights
            this.appInsights.loadAppInsights();

            // Add telemetry initializer for custom properties
            this.appInsights.addTelemetryInitializer((envelope) => {
                try {
                    // Set cloud role tags properly (tags is an object, not an array)
                    envelope.tags = envelope.tags || {};
                    envelope.tags['ai.cloud.role'] = 'insightly-archive-frontend';
                    envelope.tags['ai.cloud.roleInstance'] = config.environment || 'unknown';

                    if (envelope.baseData) {
                        envelope.baseData.properties = envelope.baseData.properties || {};

                        // Only add environment (not on every property)
                        if (!envelope.baseData.properties.environment) {
                            envelope.baseData.properties.environment = config.environment;
                        }

                        // User context only for errors/exceptions (not events)
                        if (this.userContext && envelope.baseType === 'ExceptionData') {
                            envelope.baseData.properties.userEmail = this.userContext.email;
                        }
                    }

                    // Sanitize sensitive data
                    this.sanitizeTelemetry(envelope);

                    return true;
                } catch (error) {
                    console.error('❌ Telemetry initializer error:', error);
                    return true;
                }
            });

            // Set up global error handlers
            this.setupGlobalErrorHandlers();

            this.initialized = true;
            console.log(`✅ Application Insights initialized (${config.environment})`);

            // Track initialization event
            this.trackEvent('monitoring_initialized', {
                environment: config.environment,
                appVersion: this.getAppVersion()
            });

            return true;

        } catch (error) {
            console.error('❌ Failed to initialize Application Insights:', error);
            return false;
        }
    }

    /**
     * Set up global error handlers for uncaught errors
     * @private
     */
    setupGlobalErrorHandlers() {
        // Handle uncaught errors
        window.addEventListener('error', (event) => {
            console.log('🔴 Global error handler triggered:', event.error || event.message);
            this.trackError(event.error || new Error(event.message), {
                category: ErrorCategory.UI,
                severity: ErrorSeverity.ERROR,
                type: 'uncaught_error',
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno
            });
        });

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            console.log('🔴 Unhandled rejection handler triggered:', event.reason);
            const error = event.reason instanceof Error
                ? event.reason
                : new Error(String(event.reason));

            this.trackError(error, {
                category: ErrorCategory.UI,
                severity: ErrorSeverity.ERROR,
                type: 'unhandled_promise_rejection'
            });
        });

        if (this.config?.enableDebug) {
            console.log('🔧 Global error handlers set up');
        }
    }

    /**
     * Sanitize properties to ensure all values are strings (required by Application Insights)
     * @private
     */
    sanitizeProperties(properties) {
        const sanitized = {};
        for (const [key, value] of Object.entries(properties)) {
            if (value === undefined || value === null) {
                continue; // Skip undefined/null values
            }
            // Convert all values to strings
            sanitized[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
        }
        return sanitized;
    }

    /**
     * Track an error with context
     *
     * @param {Error} error - Error object
     * @param {Object} properties - Additional properties
     * @param {string} properties.category - Error category (use ErrorCategory enum)
     * @param {number} properties.severity - Error severity (use ErrorSeverity enum)
     * @param {string} properties.operation - Operation being performed
     * @param {string} properties.entityType - Entity type (e.g., 'contact', 'project')
     * @param {string} properties.entityId - Entity ID
     * @param {Object} properties.metadata - Additional metadata
     */
    trackError(error, properties = {}) {
        if (!this.initialized || !this.appInsights) {
            console.error('Monitoring not initialized:', error);
            return;
        }

        try {
            // Ensure error is an Error object
            const errorObj = error instanceof Error ? error : new Error(String(error));

            // Build properties
            const errorProperties = {
                category: properties.category || ErrorCategory.UNKNOWN,
                severity: properties.severity || ErrorSeverity.ERROR,
                operation: properties.operation,
                ...properties // Allow caller to add specific properties
            };

            // Add user context only for critical/error severity
            if (this.userContext && (properties.severity >= ErrorSeverity.ERROR)) {
                errorProperties.userEmail = this.userContext.email;
            }

            // Sanitize properties to ensure all values are strings
            const sanitizedProperties = this.sanitizeProperties(errorProperties);

            // Track exception in Application Insights
            this.appInsights.trackException({
                exception: errorObj,
                severityLevel: properties.severity || ErrorSeverity.ERROR,
                properties: sanitizedProperties
            });

            // Log to console in debug mode
            if (this.config?.enableDebug) {
                console.error('📊 Tracked error:', errorObj, errorProperties);
            }

        } catch (trackingError) {
            console.error('❌ Failed to track error:', trackingError);
        }
    }

    /**
     * Track a custom event
     *
     * @param {string} name - Event name
     * @param {Object} properties - Event properties
     * @param {Object} measurements - Numeric measurements
     */
    trackEvent(name, properties = {}, measurements = {}) {
        if (!this.initialized || !this.appInsights) {
            if (this.config?.enableDebug) {
                console.log('Monitoring not initialized, skipping event:', name);
            }
            return;
        }

        try {
            const eventProperties = {
                ...properties
            };

            // Only add user context for specific events (not all)
            if (this.userContext && properties.includeUser) {
                eventProperties.userEmail = this.userContext.email;
            }

            // Sanitize properties to ensure all values are strings
            const sanitizedProperties = this.sanitizeProperties(eventProperties);

            this.appInsights.trackEvent({
                name: name,
                properties: sanitizedProperties,
                measurements: measurements
            });

            if (this.config?.enableDebug) {
                console.log('📊 Tracked event:', name, eventProperties);
            }

        } catch (error) {
            console.error('❌ Failed to track event:', error);
        }
    }

    /**
     * Track a page view
     *
     * @param {string} pageName - Name/title of the page being viewed
     * @param {Object} properties - Additional properties (url, entityType, entityId, etc.)
     */
    trackPageView(pageName, properties = {}) {
        if (!this.initialized || !this.appInsights) {
            return;
        }

        try {
            const pageProperties = {
                environment: this.config?.environment || 'unknown',
                ...properties
            };

            // Add user context if available
            if (this.userContext) {
                pageProperties.userEmail = this.userContext.email;
                pageProperties.userId = this.userContext.uid;
            }

            // Sanitize properties to ensure all values are strings
            const sanitizedProperties = this.sanitizeProperties(pageProperties);

            this.appInsights.trackPageView({
                name: pageName || document.title,
                uri: properties.url || window.location.href,
                properties: sanitizedProperties
            });

        } catch (error) {
            console.error('❌ Failed to track page view:', error);
        }
    }

    /**
     * Start tracking an operation (for performance monitoring)
     *
     * @param {string} operationName - Name of the operation
     * @param {Object} properties - Operation properties
     * @returns {string} - Operation ID for use with endOperation()
     */
    startOperation(operationName, properties = {}) {
        const operationId = `${operationName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        this.operationStack.set(operationId, {
            name: operationName,
            startTime: Date.now(),
            properties: properties
        });

        if (this.config?.enableDebug) {
            console.log(`⏱️ Started operation: ${operationName} [${operationId}]`);
        }

        return operationId;
    }

    /**
     * End an operation and track performance
     *
     * @param {string} operationId - Operation ID from startOperation()
     * @param {boolean} success - Whether operation succeeded
     * @param {Object} additionalProperties - Additional properties to log
     */
    endOperation(operationId, success = true, additionalProperties = {}) {
        const operation = this.operationStack.get(operationId);

        if (!operation) {
            console.warn(`⚠️ Operation not found: ${operationId}`);
            return;
        }

        const duration = Date.now() - operation.startTime;

        // 🔥 ONLY track as dependency (removed duplicate trackEvent call)
        if (this.appInsights) {
            const dependencyProperties = {
                ...operation.properties,
                ...additionalProperties
            };
            const sanitizedProperties = this.sanitizeProperties(dependencyProperties);

            this.appInsights.trackDependencyData({
                id: operationId,
                name: operation.name,
                duration: duration,
                success: success,
                responseCode: success ? '200' : '500',
                properties: sanitizedProperties
            });
        }

        this.operationStack.delete(operationId);

        if (this.config?.enableDebug) {
            console.log(`⏱️ Completed operation: ${operation.name} (${duration}ms) [${operationId}]`);
        }
    }

    /**
     * Track an API call
     *
     * @param {string} method - HTTP method
     * @param {string} url - API URL
     * @param {number} duration - Duration in milliseconds
     * @param {boolean} success - Whether call succeeded
     * @param {number} statusCode - HTTP status code
     * @param {Object} properties - Additional properties
     */
    trackAPICall(method, url, duration, success, statusCode, properties = {}) {
        if (!this.initialized || !this.appInsights) {
            return;
        }

        try {
            // Sanitize URL (remove query parameters with sensitive data)
            const sanitizedUrl = this.sanitizeUrl(url);

            const apiProperties = {
                method: method,
                url: sanitizedUrl,
                statusCode: String(statusCode),
                ...properties
            };
            const sanitizedProperties = this.sanitizeProperties(apiProperties);

            this.appInsights.trackDependencyData({
                id: `api_${Date.now()}`,
                name: `${method} ${sanitizedUrl}`,
                duration: duration,
                success: success,
                responseCode: String(statusCode), // Must be string
                type: 'HTTP',
                properties: sanitizedProperties
            });

        } catch (error) {
            console.error('❌ Failed to track API call:', error);
        }
    }

    /**
     * Set user context for all telemetry
     *
     * @param {Object} user - Firebase user object
     * @param {string} user.uid - User ID
     * @param {string} user.email - User email
     * @param {string} user.displayName - User display name
     */
    setUserContext(user) {
        if (!this.initialized || !this.appInsights) {
            return;
        }

        if (!user) {
            this.userContext = null;
            this.appInsights.clearAuthenticatedUserContext();
            return;
        }

        try {
            this.userContext = {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName
            };

            // Set authenticated user context in Application Insights
            this.appInsights.setAuthenticatedUserContext(
                user.uid,
                null, // accountId - not used
                true  // storeInCookie
            );

            if (this.config?.enableDebug) {
                console.log('👤 User context set:', user.email);
            }

            // Track user login event
            this.trackEvent('user_authenticated', {
                userEmail: user.email,
                userId: user.uid
            });

        } catch (error) {
            console.error('❌ Failed to set user context:', error);
        }
    }

    /**
     * Clear user context (on logout)
     */
    clearUserContext() {
        if (!this.initialized || !this.appInsights) {
            return;
        }

        this.userContext = null;
        this.appInsights.clearAuthenticatedUserContext();

        if (this.config?.enableDebug) {
            console.log('👤 User context cleared');
        }
    }

    /**
     * Flush telemetry (force send)
     * Useful before page unload or critical errors
     */
    flush() {
        if (this.initialized && this.appInsights) {
            this.appInsights.flush();
        }
    }

    /**
     * Sanitize telemetry to remove sensitive data
     * @private
     */
    sanitizeTelemetry(envelope) {
        const sensitiveKeys = [
            'password', 'token', 'authorization', 'auth', 'secret',
            'api_key', 'apikey', 'credential', 'private'
        ];

        const sanitizeObject = (obj) => {
            if (!obj || typeof obj !== 'object') return;

            Object.keys(obj).forEach(key => {
                const lowerKey = key.toLowerCase();

                // Remove sensitive keys
                if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
                    obj[key] = '[REDACTED]';
                }
                // Recursively sanitize nested objects
                else if (typeof obj[key] === 'object') {
                    sanitizeObject(obj[key]);
                }
                // Truncate very long strings
                else if (typeof obj[key] === 'string' && obj[key].length > 5000) {
                    obj[key] = `${obj[key].substring(0, 5000)  }... [TRUNCATED]`;
                }
            });
        };

        sanitizeObject(envelope);
    }

    /**
     * Sanitize URL to remove sensitive query parameters
     * @private
     */
    sanitizeUrl(url) {
        try {
            const urlObj = new URL(url);
            const sensitiveParams = ['token', 'auth', 'api_key', 'apikey', 'secret'];

            sensitiveParams.forEach(param => {
                if (urlObj.searchParams.has(param)) {
                    urlObj.searchParams.set(param, '[REDACTED]');
                }
            });

            return urlObj.toString();
        } catch {
            // If URL parsing fails, return original
            return url;
        }
    }

    /**
     * Get application version
     * @private
     */
    getAppVersion() {
        // Try to get version from meta tag or return default
        const versionMeta = document.querySelector('meta[name="version"]');
        return versionMeta?.content || '1.0.0';
    }

    /**
     * Check if monitoring is initialized
     */
    isInitialized() {
        return this.initialized;
    }
}

// Export singleton instance
export const monitoring = new MonitoringService();

// Export for testing
export default monitoring;
